import { useState, useEffect, useRef, useMemo } from 'react';
import { fetchQuote, PricingParams, QuoteResponse } from '@/lib/pricing-api';

export function usePricingQuote(payload: PricingParams | null) {
    const [quote, setQuote] = useState<QuoteResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    // Stable serialization to avoid re-triggering effect on every render
    const payloadKey = useMemo(
        () => (payload ? JSON.stringify(payload) : null),
        [payload?.model_id, payload?.kind, JSON.stringify(payload?.params)],
    );
    const payloadRef = useRef<string | null>(null);
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (payloadKey === payloadRef.current) {
            return;
        }
        payloadRef.current = payloadKey;

        if (!payload) {
            setQuote(null);
            return;
        }

        const reqId = ++requestIdRef.current;
        const controller = new AbortController();
        const signal = controller.signal;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                // Debounce
                await new Promise(resolve => setTimeout(resolve, 250));
                if (signal.aborted || reqId !== requestIdRef.current) return;

                const result = await fetchQuote(payload);
                if (signal.aborted || reqId !== requestIdRef.current) return;

                setQuote(result);
                setLoading(false);
            } catch (err: unknown) {
                if (signal.aborted || reqId !== requestIdRef.current) return;
                setError(err instanceof Error ? err : new Error(String(err)));
                setLoading(false);
            }
        };

        fetchData();

        return () => {
            controller.abort();
        };
    }, [payloadKey]);

    return { quote, loading, error };
}
